import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './GuestPage.module.scss';
import { formatTokenNumber } from '../../../utils/textOperations';
import { dbQueueWithCurDateRef } from '../../../api/queue';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, add } from 'date-fns';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LeaveConfirm from '../../common/Modal';
import { StandardButton } from '../../common/Button';
import { ref, get, update, onValue } from 'firebase/database';
import HeaderSection from '../../common/Header/HeaderSection';
import { db } from '../../../FirebaseConfig';
import GuestWaitingMessageBlock from './GuestWaitingMessageBlock';

function GuestWaitingStatus() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const guest = queryParams.get('guestId');
  const queueIdParam = queryParams.get('queueId');

  const [show, setShow] = useState(false);
  const [guestId, setGuestId] = useState(guest);
  const [queueId, setQueueId] = useState(queueIdParam);
  const [updateToken, setUpdateToken] = useState({});
  const [isInQueue, setIsInQueue] = useState(false);

  const unsubscribeRef = useRef();

  const subscribe = useCallback(() => {
    if (!queueId || !guestId) return;

    const unsubscribe = onValue(
      dbQueueWithCurDateRef(queueId, `/guests/${guestId}/updatedAt`),
      async (data) => {
        if (data.val()) {
          try {
            const date = format(Date.now(), 'yyyy-MM-dd');
            console.log(date);
            const snapshotQueue = await get(
              ref(db, `queues/${queueId}/daily_stats/${date}`)
            );
            console.log(snapshotQueue.val());
            const avgWaitTimePerPosition =
              snapshotQueue.val()?.averageWaitTimePerPosition;
            console.log(avgWaitTimePerPosition);

            const snapshotGuest = await get(
              dbQueueWithCurDateRef(queueId, `/guests/${guestId}`)
            );
            const guestData = snapshotGuest.val();

            if (guestData) {
              let estWaitTime = 0;
              let expTime = 0;
              if (!avgWaitTimePerPosition) {
                estWaitTime = null;
                expTime = null;
              } else {
                estWaitTime = Math.round(
                  guestData.position * avgWaitTimePerPosition
                );
                expTime = format(
                  add(new Date(), { minutes: estWaitTime }),
                  'HH:mm'
                );
              }
              setUpdateToken({
                ...guestData,
                estWaitTime,
                expTime,
              });

              setIsInQueue(
                guestData.status.startsWith('waiting') &&
                  guestData.position >= 0
              );
            }
          } catch (error) {
            console.error('Error: ', error.message);
          }
        }
      }
    );

    unsubscribeRef.current = unsubscribe;
  }, [guestId, queueId]);

  const fetchGuestData = useCallback(async () => {
    if (!queueId || !guestId) {
      console.error('queueId или guestId отсутствует!');
      return;
    }

    try {
      const guestRef = dbQueueWithCurDateRef(queueId, `/guests/${guestId}`);
      const snapshot = await get(guestRef);

      if (snapshot.exists()) {
        setUpdateToken(snapshot.val());
        setIsInQueue(
          snapshot.val().status.startsWith('waiting') &&
            snapshot.val().position >= 0
        );
      } else {
        console.error('No guest found!');
      }
    } catch (error) {
      console.error('Error when getting guest data', error.message);
    }
  }, [guestId, queueId]);

  useEffect(() => {
    fetchGuestData();
    subscribe();

    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [fetchGuestData, subscribe]);

  const navigate = useNavigate();
  const handleLeaveQueue = async () => {
    if (updateToken) {
      try {
        const activeRef = dbQueueWithCurDateRef(updateToken.queueId, '/guests');
        const snapshot = await get(activeRef);
        if (
          snapshot &&
          (snapshot.val()[updateToken.id].status === 'waiting' ||
            snapshot.val()[updateToken.id].status === 'waiting-notified')
        ) {
          const updatePositions = {};
          updatePositions[`/${updateToken.id}/status`] = 'canceled';
          const curDate = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
          const updatedList = Object.values(snapshot.val()).filter(
            (value) =>
              value.status === 'waiting' ||
              value.status === 'waiting - notified'
          );
          updatedList.forEach((t) => {
            if (t.position > updateToken.position) {
              updatePositions[`/${t.id}/position`] = t.position - 1;
              updatePositions[`/${t.id}/updatedAt`] = curDate;
            }
          });

          await update(activeRef, updatePositions);
        }
        handleClose();
        navigate('/join');
      } catch (error) {
        console.error('\tError by: ', error.message);
      }
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);
  if (updateToken)
    return (
      <div>
        <HeaderSection
          title={updateToken.queueName}
          subTitle={updateToken.queueDesc}
        />
        <div className={'container mt-4'}>
          <div className={styles['status-box']}>
            <div className={styles['waiting-logo']}>
              <img src="/images/joining-queue-48.png" width={'30px'} />
              <h3>ID - {formatTokenNumber(updateToken.number)}</h3>
            </div>
            <h5>Hello {updateToken.name}, </h5>
            {/* {isInQueue && (
              <p className={styles['info-message']}>
                You are now in queue for this event.
              </p>
            )} */}
            <GuestWaitingMessageBlock updateToken={updateToken} />
            <p style={{ fontStyle: 'italic', paddingTop: '20px' }}>
              Updated at {updateToken.updatedAt}
            </p>
            <div className={styles['button-container']}>
              {isInQueue && (
                <StandardButton
                  className={styles['leave-btn']}
                  variant="contained"
                  icon={<ExitToAppIcon />}
                  onClick={() => setShow(true)}
                >
                  Leave
                </StandardButton>
              )}
              {show && (
                <LeaveConfirm
                  title="Leave Event"
                  closeText="Cancel"
                  submitText={'Leave'}
                  onClose={handleClose}
                  onSubmit={handleLeaveQueue}
                >
                  <div className={'modal-body confirmation-message'}>
                    Your turn is coming soon. Are you sure you want to leave
                    this event?
                  </div>
                </LeaveConfirm>
              )}
            </div>
          </div>
        </div>
      </div>
    );
}

export default GuestWaitingStatus;
